/**
 * Chat Agent Config Page.
 * Create or edit a chat agent's configuration: name, description, model, system prompt, tools.
 */

import { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router';
import { useOrgNavigate } from '@/hooks/use-org-navigate';
import { Save, MessageSquare, Unplug } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  MultiSelectAutocomplete,
  type MultiSelectAutocompleteOption,
} from '@/components/ui/multi-select-autocomplete';
import { ROUTES } from '@/constants/routes';
import { useOrgStore } from '@/stores/org-store';
import { useChatAgentsStore } from '@/stores/chat-agents-store';
import {
  getChatAgent,
  createChatAgent,
  updateChatAgent,
  getAvailableToolGroups,
  disconnectSlack,
} from '@/services/chat-agents/agents';
import type {
  ChatAgent,
  ChatAgentCreateRequest,
  ToolGroup,
} from '@/types/chat-agent-types';

// URL for the Slack Agent Service (OAuth install endpoint)
const SLACK_SERVICE_URL =
  import.meta.env.VITE_SLACK_SERVICE_URL || 'http://localhost:8350';
import { AgentIconColorPicker } from './components/agent-icon-color-picker';
import { generateAgentName } from '@/stores/helpers/agent-naming';

const DEFAULT_TOOL_GROUPS = ['workflow-builder', 'composio'];

// Models available for LibreChat agents
// Only models with configured API keys will work
const AVAILABLE_MODELS = [
  { label: 'Claude Opus 4.6', value: 'claude-opus-4-6' },
  { label: 'Claude Sonnet 4.5', value: 'claude-sonnet-4-5-20250929' },
  { label: 'Claude Sonnet 4', value: 'claude-sonnet-4-20250514' },
  { label: 'Claude 3.7 Sonnet', value: 'claude-3-7-sonnet-20250219' },
  { label: 'Claude 3.5 Haiku', value: 'claude-3-5-haiku-20241022' },
];

export function ChatAgentConfigPage() {
  const { agentId } = useParams<{ agentId: string }>();
  const navigate = useOrgNavigate();
  const currentOrg = useOrgStore((s) => s.currentOrg);
  const setCurrentAgent = useChatAgentsStore((s) => s.setCurrentAgent);
  const isNew = !agentId || agentId === 'new';

  const [name, setName] = useState(() => (isNew ? generateAgentName() : ''));
  const [description, setDescription] = useState('');
  const [model, setModel] = useState('claude-opus-4-6');
  const [icon, setIcon] = useState('');
  const [color, setColor] = useState('');
  const [systemPrompt, setSystemPrompt] = useState(
    'You are a helpful assistant with access to MCP tools. Use them when appropriate to help the user. If a User asks for data visualization nodes you should make sure to try to use the D3 Data Visualization output node, the network graph output nodes, or make sure to use the Data Analysis agent type which can make artifacts when building workflows. You specialize in using workflow building tools. You should use opus 4.6 when building workflows with the builder tools. You should ALWAYS list nodes and inspect nodes before building to make a good decision about what to use. You should try to never use markdown as an output if a data visualization, spreadsheet, powerpoint is a better choice and you should use the appropriate node in the workflow. When a user asks for something that is not a workflow and a workflow could make the artifact, ask the user if they want to create a workflow. Also make sure to use rich visual outputs - if they ask for a dashboard, always use the d3 node or data analysis node for output.',
  );
  const [selectedToolGroups, setSelectedToolGroups] =
    useState<string[]>(DEFAULT_TOOL_GROUPS);
  const [availableToolGroups, setAvailableToolGroups] = useState<ToolGroup[]>(
    [],
  );
  const [isLoadingTools, setIsLoadingTools] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(!isNew);
  const [error, setError] = useState<string | null>(null);

  // Slack integration state
  const [slackTeamId, setSlackTeamId] = useState('');
  const [slackTeamName, setSlackTeamName] = useState('');
  const [slackInstalledAt, setSlackInstalledAt] = useState('');
  const [isDisconnectingSlack, setIsDisconnectingSlack] = useState(false);
  const [searchParams] = useSearchParams();
  const slackJustConnected = searchParams.get('slack') === 'connected';
  // Store the loaded agent for referencing its ID
  const [loadedAgent, setLoadedAgent] = useState<ChatAgent | null>(null);

  const toolGroupOptions = useMemo<MultiSelectAutocompleteOption[]>(
    () =>
      availableToolGroups
        .map((g) => ({
          value: g.id,
          label: g.label,
          description: `${g.tool_count} tools${g.category ? ` · ${g.category}` : ''}${g.description ? ` — ${g.description}` : ''}`,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [availableToolGroups],
  );

  // Load available tool groups from mcp-servers-runner (via platform API)
  useEffect(() => {
    const orgId = currentOrg?.orgid;
    if (!orgId) return;
    const loadToolGroups = async () => {
      setIsLoadingTools(true);
      try {
        const groups = await getAvailableToolGroups(orgId);
        setAvailableToolGroups(groups);
        if (isNew) {
          // Drop default groups the org doesn't have access to
          const groupIds = new Set(groups.map((g) => g.id));
          setSelectedToolGroups((prev) =>
            prev.filter((id) => groupIds.has(id)),
          );
        }
      } catch {
        console.warn('Failed to load tool groups, using empty list');
      } finally {
        setIsLoadingTools(false);
      }
    };
    loadToolGroups();
  }, [currentOrg?.orgid, isNew]);

  // Load existing agent data for editing
  useEffect(() => {
    const loadAgent = async () => {
      if (isNew || !currentOrg?.orgid || !agentId) return;
      setIsLoading(true);
      try {
        const agent = await getChatAgent(currentOrg.orgid, agentId);
        if (agent) {
          setLoadedAgent(agent);
          setCurrentAgent(agent);
          setName(agent.name);
          setDescription(agent.description);
          setModel(agent.model || 'claude-opus-4-6');
          if (agent.icon) setIcon(agent.icon);
          if (agent.color) setColor(agent.color);
          setSystemPrompt(agent.system_prompt || '');
          // Determine which tool groups are selected based on agent's tools
          const agentTools = agent.tools || [];
          const groups = availableToolGroups
            .filter((g) => g.tools.some((t) => agentTools.includes(t)))
            .map((g) => g.id);
          setSelectedToolGroups(groups.length > 0 ? groups : []);
          // Slack fields
          setSlackTeamId(agent.slack_team_id || '');
          setSlackTeamName(agent.slack_team_name || '');
          setSlackInstalledAt(agent.slack_installed_at || '');
        }
      } catch {
        setError('Failed to load agent');
      } finally {
        setIsLoading(false);
      }
    };
    loadAgent();
  }, [isNew, currentOrg?.orgid, agentId, availableToolGroups]);

  const handleSave = async () => {
    if (!currentOrg?.orgid || !name.trim()) return;
    setIsSaving(true);
    setError(null);

    try {
      // Collect all tools and MCP server names from selected groups
      const selectedGroups = availableToolGroups.filter((g) =>
        selectedToolGroups.includes(g.id),
      );
      const selectedTools = selectedGroups.flatMap((g) => g.tools);

      const data: ChatAgentCreateRequest = {
        name: name.trim(),
        description: description.trim(),
        model,
        system_prompt: systemPrompt.trim(),
        tools: selectedTools,
        icon,
        color,
      };

      if (isNew) {
        const created = await createChatAgent(currentOrg.orgid, data);
        if (created) {
          navigate(ROUTES.CHAT_AGENTS.CHAT.replace(':agentId', created.id));
        }
      } else if (agentId) {
        await updateChatAgent(currentOrg.orgid, agentId, data);
        navigate(ROUTES.CHAT_AGENTS.ROOT);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save agent');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className='mx-auto flex w-full max-w-2xl flex-col'>
        {/* Header skeleton */}
        <div className='mb-8'>
          <Skeleton className='h-8 w-48' />
        </div>
        <div className='space-y-6'>
          {/* Name */}
          <div className='space-y-2'>
            <Skeleton className='h-4 w-12' />
            <div className='flex items-center gap-3'>
              <Skeleton className='size-10 rounded-lg' />
              <Skeleton className='h-10 flex-1 rounded-md' />
            </div>
          </div>
          {/* Description */}
          <div className='space-y-2'>
            <Skeleton className='h-4 w-24' />
            <Skeleton className='h-20 w-full rounded-md' />
          </div>
          {/* Model */}
          <div className='space-y-2'>
            <Skeleton className='h-4 w-14' />
            <Skeleton className='h-10 w-52 rounded-md' />
          </div>
          {/* System Prompt */}
          <div className='space-y-2'>
            <Skeleton className='h-4 w-28' />
            <Skeleton className='h-36 w-full rounded-md' />
          </div>
          {/* MCP Tools */}
          <div className='space-y-3'>
            <Skeleton className='h-4 w-20' />
            <Skeleton className='h-4 w-64' />
            <Skeleton className='h-16 w-full rounded-lg' />
            <Skeleton className='h-16 w-full rounded-lg' />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='mx-auto flex w-full max-w-2xl flex-col'>
      {/* Header actions rendered via portal into the layout header */}
      {/* Header */}
      <div className='mb-8 flex items-center justify-between'>
        <h1 className='text-2xl font-bold'>
          {isNew ? 'Create Chat Agent' : 'Edit Chat Agent'}
        </h1>
        <div className='flex gap-3'>
          <Button
            variant='outline'
            onClick={() =>
              navigate(
                isNew
                  ? ROUTES.CHAT_AGENTS.ROOT
                  : ROUTES.CHAT_AGENTS.CHAT.replace(':agentId', agentId || ''),
              )
            }
          >
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isSaving || !name.trim()}>
            <Save className='mr-2 size-4' />
            {isSaving ? 'Saving...' : isNew ? 'Create Agent' : 'Save Changes'}
          </Button>
        </div>
      </div>

      {error && (
        <div className='rounded-lg border border-destructive bg-destructive/10 p-4 mb-6'>
          <p className='text-sm text-destructive'>{error}</p>
        </div>
      )}

      <div className='space-y-6'>
        {/* Icon, Color & Name */}
        <div className='space-y-2'>
          <Label htmlFor='name'>Name</Label>
          <div className='flex items-center gap-3'>
            <AgentIconColorPicker
              icon={icon}
              color={color}
              onIconChange={setIcon}
              onColorChange={setColor}
            />
            <Input
              id='name'
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder='e.g., Earnings Quality Specialist'
              maxLength={100}
              className='flex-1'
            />
          </div>
        </div>

        {/* Description */}
        <div className='space-y-2'>
          <Label htmlFor='description'>Description</Label>
          <Textarea
            id='description'
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder='What does this agent do?'
            rows={2}
            maxLength={500}
          />
        </div>

        {/* Model */}
        <div className='space-y-2'>
          <Label htmlFor='model'>Model</Label>
          <Select value={model} onValueChange={setModel}>
            <SelectTrigger>
              <SelectValue placeholder='Select a model' />
            </SelectTrigger>
            <SelectContent>
              {AVAILABLE_MODELS.map((m) => (
                <SelectItem key={m.value} value={m.value}>
                  {m.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* System Prompt */}
        <div className='space-y-2'>
          <Label htmlFor='system-prompt'>System Prompt</Label>
          <Textarea
            id='system-prompt'
            value={systemPrompt}
            onChange={(e) => setSystemPrompt(e.target.value)}
            placeholder='You are a helpful assistant...'
            rows={6}
            className='font-mono text-sm'
          />
        </div>

        {/* MCP Tools */}
        <div className='space-y-3'>
          <Label>MCP Tools</Label>
          <p className='text-xs text-muted-foreground'>
            Select which tool capabilities this agent should have access to.
          </p>
          {isLoadingTools ? (
            <div className='text-sm text-muted-foreground animate-pulse py-4'>
              Discovering available tools...
            </div>
          ) : availableToolGroups.length === 0 ? (
            <div className='text-sm text-muted-foreground py-4'>
              No MCP tool servers found. Check that mcp-servers-runner is
              running.
            </div>
          ) : (
            <MultiSelectAutocomplete
              value={selectedToolGroups}
              onChange={setSelectedToolGroups}
              options={toolGroupOptions}
              placeholder='Search MCP tool servers…'
              emptyMessage='No MCP tool servers found.'
              strict
            />
          )}
        </div>

        {/* Slack Integration (only shown when editing an existing agent) */}
        {!isNew && loadedAgent && (
          <div className='space-y-3'>
            <Label>Slack Integration</Label>
            {slackTeamId ? (
              <div className='rounded-lg border border-border p-4 space-y-3'>
                <div className='flex items-center gap-3'>
                  <MessageSquare className='h-5 w-5 text-[#4A154B]' />
                  <div className='flex-1'>
                    <div className='text-sm font-medium'>
                      Connected to {slackTeamName || slackTeamId}
                    </div>
                    <div className='text-xs text-muted-foreground'>
                      {slackInstalledAt
                        ? `Installed ${new Date(slackInstalledAt).toLocaleDateString()}`
                        : 'Slack workspace connected'}
                    </div>
                  </div>
                  <Button
                    variant='outline'
                    size='sm'
                    onClick={async () => {
                      if (!currentOrg?.orgid || !agentId) return;
                      setIsDisconnectingSlack(true);
                      try {
                        const updated = await disconnectSlack(
                          currentOrg.orgid,
                          agentId,
                        );
                        if (updated) {
                          setSlackTeamId('');
                          setSlackTeamName('');
                          setSlackInstalledAt('');
                        }
                      } catch (e) {
                        setError(
                          e instanceof Error
                            ? e.message
                            : 'Failed to disconnect Slack',
                        );
                      } finally {
                        setIsDisconnectingSlack(false);
                      }
                    }}
                    disabled={isDisconnectingSlack}
                    className='gap-1.5 text-destructive hover:text-destructive'
                  >
                    <Unplug className='h-3.5 w-3.5' />
                    {isDisconnectingSlack ? 'Disconnecting...' : 'Disconnect'}
                  </Button>
                </div>
                {slackJustConnected && (
                  <div className='rounded-md bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 p-3'>
                    <p className='text-xs text-green-700 dark:text-green-400'>
                      Slack connected successfully. Users in this workspace can
                      now DM the bot or @mention it in channels.
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <div className='rounded-lg border border-dashed border-border p-4 space-y-3'>
                <p className='text-sm text-muted-foreground'>
                  Connect this agent to a Slack workspace so users can chat with
                  it via DMs or @mentions in channels.
                </p>
                <Button
                  variant='outline'
                  onClick={() => {
                    const redirectUrl = window.location.href.split('?')[0];
                    const installUrl = new URL(
                      '/slack/install',
                      SLACK_SERVICE_URL,
                    );
                    if (agentId) {
                      installUrl.searchParams.set('agent_id', agentId);
                    }
                    if (currentOrg?.orgid) {
                      installUrl.searchParams.set(
                        'orgid',
                        String(currentOrg.orgid),
                      );
                    }
                    installUrl.searchParams.set('redirect', redirectUrl);
                    window.location.href = installUrl.toString();
                  }}
                  className='gap-2'
                >
                  <MessageSquare className='h-4 w-4' />
                  Install to Slack
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
